import i18n from "../i18n";import { Trans } from "react-i18next";import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Copy,
  KeyRound,
  X } from
'lucide-react';
import { AppLayout } from '../components/layout';
import sessionDB, { saveSession } from '../utils/sessionDB';
import { UserPreferencesModal } from '../components/layout/UserPreferencesModal';
import { getQuickLinks, QUICK_LINK_ICONS, DefaultQuickLinkIcon, ConfigureQuickLinkIcon } from '../utils/quickLinks';
import { useTranslation } from "react-i18next";

/* ─── Session storage ──────────────────────────────────────────────────
 * WelcomePage used to keep its own private copy of the session (a hand
 * picked subset written to a `wp_session` sessionStorage key). That has
 * been replaced by the shared sessionDB util (src/utils/sessionDB.js),
 * which is now the single place the whole app — this page, utils/api.js,
 * and every merged HRMS/ESS module under src/api, src/lib and
 * src/services — reads and writes the session from.
 * ────────────────────────────────────────────────────────────────────── */
function getSessionView() {
  return {
    tenantCode: sessionDB.getTenantCode(),
    empId: sessionDB.getEmpId(),
    canonicalRoleName: sessionDB.getCanonicalRoleName(),
    accessToken: sessionDB.getAccessToken(),
    refreshToken: sessionDB.getRefreshToken()
  };
}

const CARD =
'bg-card rounded-2xl shadow-[0_8px_28px_-16px_rgba(36,51,72,0.18)] border border-border';

function getGreeting(hour) {
  if (hour < 12) return 'dashboard.goodMorning';
  if (hour < 17) return 'dashboard.goodAfternoon';
  return 'dashboard.goodEvening';
}
 

/* ─── Toast hook ───────────────────────────────────────── */
function useToast() {
  const [msg, setMsg] = useState('');
  const [visible, setVisible] = useState(false);
  const show = useCallback((text, duration = 2500) => {
    setMsg(text);
    setVisible(true);
    setTimeout(() => setVisible(false), duration);
  }, []);
  return { msg, visible, show };
}

function Toast({ msg, visible }) {
  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-500 bg-foreground text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg transition-opacity pointer-events-none whitespace-nowrap ${
      visible ? 'opacity-100' : 'opacity-0'}`
      }
      role="status"
      aria-live="polite">

      {msg}
    </div>);

}

/* ─── Session toggle button (footer) ────────────────────── */
/* ─── Session Info Panel ───────────────────────────────── */
function SessionPanel({ onClose }) {
  const session = getSessionView();
  const [copied, setCopied] = useState('');

  const fields = [
  { label: 'Tenant Code', key: 'tenantCode', value: session.tenantCode },
  { label: 'Employee ID', key: 'empId', value: session.empId },
  { label: 'Canonical Role Name', key: 'canonicalRoleName', value: session.canonicalRoleName },
  { label: 'Access Token', key: 'accessToken', value: session.accessToken, truncate: true },
  { label: 'Refresh Token', key: 'refreshToken', value: session.refreshToken, truncate: true }];


  function handleCopy(key, value) {
    navigator.clipboard.writeText(value || '').then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    });
  }

  return (
    <>
      <div
        className="fixed inset-0 z-298"
        onClick={onClose}
        aria-hidden="true" />

      <div className="fixed bottom-[76px] left-1/2 -translate-x-1/2 w-[min(320px,calc(100vw-2rem))] z-299 bg-white rounded-2xl border border-(--color-text-border)/60 shadow-[0_12px_40px_rgba(30,51,131,0.18)] overflow-hidden animate-in fade-in slide-in-from-bottom-1 duration-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-primary">
          <div className="flex items-center gap-2 text-sm font-bold text-white">
            <KeyRound className="h-4 w-4" />
            <span><Trans i18nKey="dashboard.sessionInfo" /></span>
          </div>
          <button
            type="button"
            className="h-7 w-7 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white grid place-items-center transition"
            onClick={onClose}
            aria-label={i18n.t("formActions.closeSessionPanel")}>

            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col">
          {fields.map((f) =>
          <div
            key={f.key}
            className="px-4 py-2.5 border-b border-(--color-text-border)/40 last:border-b-0 flex flex-col gap-1">

              <p className="text-[10px] font-bold per-file-hint uppercase tracking-wider">
                {f.label}
              </p>
              <div className="flex items-center justify-between gap-2">
                <p
                className={`text-sm font-semibold text-foreground flex-1 ${
                f.truncate ? 'font-mono text-xs text-primary truncate max-w-[220px]' : 'break-all'}`
                }>

                  {f.value || <span className="text-(--color-text-border) font-normal">—</span>}
                </p>
                {f.value &&
              <button
                type="button"
                className={`h-7 min-w-7 px-1.5 rounded-lg border text-xs font-bold flex items-center justify-center shrink-0 transition ${
                copied === f.key ?
                'bg-emerald-50 text-emerald-600 border-emerald-200' :
                'bg-workspace/40 border-(--color-text-border) per-file-hint hover:text-primary hover:border-primary/40'}`
                }
                onClick={() => handleCopy(f.key, f.value)}
                title={i18n.t("formActions.copyToClipboard")}>

                    {copied === f.key ? '✓' : <Copy className="h-3.5 w-3.5" />}
                  </button>
              }
              </div>
            </div>
          )}
        </div>
        <p className="m-0 px-4 py-2 text-[10.5px] per-file-hint text-center border-t border-(--color-text-border)/40 bg-workspace/20">
          <Trans i18nKey="dashboard.storedInSessionstorageClearedOnTabClose" />
        </p>
      </div>
    </>);

}

/* ─── Main WelcomePage ─────────────────────────────────── */
export default function WelcomePage({ data }) {
  const { t } = useTranslation();
  const emp = data.empobj || {};
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const [sessionOpen, setSessionOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState(() => sessionDB.getEmpPreferences());

  const toast = useToast();
  const navigate = useNavigate();

  /* ── Save session on mount ────────────────────────────── */
  useEffect(() => {
    saveSession(data);
    setPreferences(sessionDB.getEmpPreferences());
  }, [data]);

  useEffect(() => {
    const refreshPreferences = () => setPreferences(sessionDB.getEmpPreferences());
    window.addEventListener('emp-preferences-updated', refreshPreferences);
    return () => window.removeEventListener('emp-preferences-updated', refreshPreferences);
  }, []);

  const initials = ((emp.first_name || 'U')[0] + (emp.last_name ? emp.last_name[0] : '')).toUpperCase();

  const overlay =
  <>
      <UserPreferencesModal open={preferencesOpen} onOpenChange={setPreferencesOpen} />
      {sessionOpen && <SessionPanel onClose={() => setSessionOpen(false)} />}
      <Toast msg={toast.msg} visible={toast.visible} />
    </>;


  /* ── Content renderer ─────────────────────────────────── */
  function renderContent() {
    const empFields = [
    { label: 'Employee ID', value: emp.emp_id },
    { label: 'Designation', value: emp.designation_name },
    { label: 'Department', value: emp.department_name },
    // { label: 'Grade',       value: emp.grade_level_name },
    //{ label: 'Division',    value: emp.division_name },
    // { label: 'Location',    value: emp.work_location_name },
    { label: 'Location', value: emp.work_location_name }]
    .filter((f) => f.value);
    const today = new Intl.DateTimeFormat(i18n.resolvedLanguage === 'en' ? 'en-IN' : (i18n.resolvedLanguage || 'en-IN'), {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    }).format(now);
    return (
      <div className="max-w-5xl mx-auto w-full min-w-0 flex flex-col gap-5 pb-4 sm:gap-6">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="wrap-break-word text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                {t(getGreeting(now.getHours()))}, {emp.first_name || 'User'}
              </h1>
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <p className="flex items-center gap-2 text-base per-file-hint sm:text-lg">
                <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />{today}
              </p>
            </div>
          </header>

          <div className={`${CARD} overflow-hidden`}>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 px-4 sm:px-6 py-4">
              {getQuickLinks(preferences).map((q, index) => {
              const label = q.to ? q.label : t('dashboard.addShortcut', { defaultValue: 'Add shortcut' });
              const Icon = q.to ? (QUICK_LINK_ICONS[q.label] || DefaultQuickLinkIcon) : ConfigureQuickLinkIcon;
              return (
                <button
                  key={q.key}
                  type="button"
                  className={`group flex min-h-[44px] min-w-0 items-center gap-2 px-3 py-2 rounded-xl border border-(--color-text-border)/60 bg-muted/50 hover:bg-card hover:shadow-[0_10px_24px_-14px_rgba(79,70,229,0.25)] hover:border-primary/30 transition text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${q.to ? '' : 'border-dashed'}`}
                  aria-label={q.to ? q.label : t('dashboard.addShortcutNumber', { defaultValue: 'Add shortcut {{number}}', number: index + 1 })}
                  title={label} onClick={() => q.to ? navigate(q.to) : setPreferencesOpen(true)}>

                    <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <p className="min-w-0 flex-1 text-sm font-semibold leading-snug text-foreground wrap-anywhere">{label}</p>
                  </button>);

            })}
            </div>
          </div>

          {/*<div className={`${CARD} p-4 sm:p-6`}>
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-(--color-text-border)/50">
              <div className="h-12 w-12 rounded-2xl bg-primary text-white text-lg font-bold grid place-items-center shrink-0">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="wrap-break-word text-base font-bold text-foreground">
                  {emp.first_name} {emp.last_name || ''}
                </p>
                <p className="whitespace-normal wrap-anywhere text-sm per-file-hint mt-0.5">
                  {emp.canonical_role_name || '—'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {empFields.map((f) =>
            <div key={f.label} className="min-w-0">
                  <p className="text-[10px] font-bold per-file-hint uppercase tracking-wider mb-0.5">
                    {f.label}
                  </p>
                  <p className="wrap-break-word text-sm font-semibold text-foreground">{f.value}</p>
                </div>
            )}
            </div>
          </div>*/}

          {/*<SessionFooterToggle sessionOpen={sessionOpen} onSessionToggle={toggleSession} />*/}
        </div>);

  }

  return (
    <AppLayout overlay={overlay} hideBreadcrumb>
      {renderContent()}
    </AppLayout>);

}
